import styled from 'styled-components';
import { msColors } from 'src/style/themes/defaultTheme';

// The screen's own palette (src/style/themes/defaultTheme.ts): its blue for pickup, its yellow for drop
const PICKUP_COLOR = '#1976d2'; // the theme blue #2196F3 one step darker, so white text passes AA (4.6:1)
const DROP_COLOR = msColors.yellow; // #FFD42A
const DROP_TEXT = msColors.darkTextcolor; // #333333 on the yellow: 8.9:1
const DROP_BORDER = '#9c7c00'; // the yellow darkened so an unpressed button's edge shows on white (3.9:1)
const WARN_TEXT = '#8a4b08';
const BORDER = '#e0e0e0';
const SOFT_BORDER = '#eeeeee';
const HEADER_BG = '#eef4fc';
const NAME_COL_WIDTH = '96px';
const WIDE_NAME_COL_WIDTH = '160px';
const INDEX_COL_MIN_WIDTH = '112px';

const kindColor = (kind: 'pickup' | 'drop') => (kind === 'pickup' ? PICKUP_COLOR : DROP_COLOR);
const kindBorder = (kind: 'pickup' | 'drop') => (kind === 'pickup' ? PICKUP_COLOR : DROP_BORDER);
/** Text on the filled (pressed) button */
const kindTextOnFill = (kind: 'pickup' | 'drop') => (kind === 'pickup' ? 'white' : DROP_TEXT);
/** Text on the white (unpressed) button */
const kindTextOnWhite = (kind: 'pickup' | 'drop') => (kind === 'pickup' ? PICKUP_COLOR : DROP_TEXT);

// In the panel: fills the space left between the days picker and the absolute footer. In the addresses
// dialog: fills the dialog. Only the rows scroll.
export const Section = styled.section<{ $inDialog?: boolean }>`
    flex: 1;
    min-height: 0;
    margin: ${({ $inDialog }) => ($inDialog ? '0' : '15px 0 70px')};
    display: flex;
    flex-direction: column;
    border: 1px solid ${BORDER};
    border-radius: 8px;
    overflow: hidden;
`;

export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
`;

export const SectionTitle = styled.h3`
    margin: 0;
    font-size: 15px;
    font-weight: 700;
`;

export const Legend = styled.div`
    display: flex;
    gap: 10px;
    font-size: 12px;
    color: #424242;
`;

export const LegendItem = styled.span<{ $kind: 'pickup' | 'drop' }>`
    display: flex;
    align-items: center;
    gap: 4px;

    &::before {
        content: '';
        width: 12px;
        height: 12px;
        border-radius: 3px;
        background: ${({ $kind }) => kindColor($kind)};
        border: 1px solid ${({ $kind }) => kindBorder($kind)};
    }
`;

// Scrolls sideways inside itself when a passenger has many addresses
export const Grid = styled.div`
    flex: 1;
    // At least the header and one passenger row stay visible when notes take the space
    min-height: 140px;
    overflow: auto;
`;

export const Row = styled.div<{ $columns: number; $isHeader?: boolean; $isWarned?: boolean; $wide?: boolean }>`
    display: grid;
    grid-template-columns: ${({ $wide }) => ($wide ? WIDE_NAME_COL_WIDTH : NAME_COL_WIDTH)} repeat(
            ${({ $columns }) => $columns},
            minmax(${INDEX_COL_MIN_WIDTH}, 1fr)
        );
    min-width: fit-content;
    border-bottom: 1px solid ${({ $isHeader }) => ($isHeader ? BORDER : SOFT_BORDER)};
    background: ${({ $isHeader, $isWarned }) => {
        if ($isHeader) return HEADER_BG;
        return $isWarned ? '#fffbf2' : 'white';
    }};
    ${({ $isHeader }) => ($isHeader ? `position: sticky; top: 0; z-index: 1; border-top: 1px solid ${BORDER};` : '')}
`;

export const NameCell = styled.div`
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 13px;

    small {
        color: #616161;
        font-size: 12px;
    }
`;

export const Cell = styled.div`
    padding: 8px 6px;
    border-inline-start: 1px solid ${SOFT_BORDER};
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const ColumnTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    text-align: center;
`;

export const ButtonsRow = styled.div`
    display: flex;
    gap: 4px;
`;

export const KindButton = styled.button<{ $kind: 'pickup' | 'drop'; $pressed?: boolean }>`
    flex: 1;
    min-height: 28px;
    padding: 0 2px;
    border: 1px solid ${({ $kind }) => kindBorder($kind)};
    border-radius: 4px;
    background: ${({ $kind, $pressed }) => ($pressed ? kindColor($kind) : 'white')};
    color: ${({ $kind, $pressed }) => ($pressed ? kindTextOnFill($kind) : kindTextOnWhite($kind))};
    font-weight: ${({ $pressed }) => ($pressed ? 700 : 400)};
    font-size: 11px;
    font-family: inherit;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${PICKUP_COLOR};
        outline-offset: 1px;
    }

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }
`;

export const AddressText = styled.div`
    font-size: 12px;
    line-height: 1.35;
    min-height: 32px;

    span {
        color: #616161;
    }
`;

export const EmptyCell = styled.div`
    flex: 1;
    min-height: 62px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    font-size: 12px;
    color: #616161;
    background: repeating-linear-gradient(135deg, #fafafa 0 6px, #f2f2f2 6px 12px);
`;

export const Note = styled.div`
    padding: 6px 12px;
    font-size: 12px;
    color: ${WARN_TEXT};
    background: #fff4e5;
`;

export const WarnText = styled.span`
    color: ${WARN_TEXT};
    font-size: 11px;
`;

export const Hint = styled.div`
    padding: 6px 12px;
    font-size: 12px;
    color: #424242;
    background: #fafafa;
    border-top: 1px solid ${SOFT_BORDER};
`;

export const Loading = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
`;

// --- The addresses dialog (design B, owner's choice 2026-11-01): the main orders table's language — its light
// blue header, pill toggles in the shift colours, a changed row in light yellow. The panel keeps the look above.
export const DIALOG_TEXT = '#1F2A33';
const DIALOG_MUTED = '#5F6B73'; // 5.47:1 on white, 5.27:1 on the changed row, 4.79:1 on the header
const DIALOG_BORDER = '#E3E8EE';
const DIALOG_ROW_BORDER = '#EEF1F5';
const DIALOG_HEADER_BG = '#E3F2FD';
const DIALOG_HEADER_RULE = '#CFE3F7';
const DIALOG_CHANGED_BG = '#FFFBEA';

/** Off / on fill and edge of a pill; the text stays DIALOG_TEXT, at least 8.3:1 on every fill */
const PILL = {
    pickup: { bg: '#E3F2FD', border: '#BBDEFB', onBg: '#90CAF9', onBorder: PICKUP_COLOR },
    drop: { bg: '#FFF8DC', border: '#F0DC8C', onBg: DROP_COLOR, onBorder: '#B38F00' },
};
/** The "for all" pills on white: the kind's edge, and text dark enough for AA (5.75:1 and 7.34:1) */
const FOR_ALL = {
    pickup: { border: '#90CAF9', text: '#1565C0' },
    drop: { border: '#E6C200', text: '#6B5300' },
};

export const DialogSection = styled.section`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid ${DIALOG_BORDER};
    border-radius: 10px;
    overflow: hidden;
    color: ${DIALOG_TEXT};
`;

export const DialogGrid = styled.div`
    flex: 1;
    min-height: 140px;
    overflow: auto;
`;

export const DialogRow = styled.div<{ $columns: number; $isHeader?: boolean; $isChanged?: boolean; $isWarned?: boolean }>`
    display: grid;
    grid-template-columns: 200px repeat(${({ $columns }) => $columns}, minmax(220px, 1fr));
    min-width: fit-content;
    border-top: ${({ $isHeader }) => ($isHeader ? '0' : `1px solid ${DIALOG_ROW_BORDER}`)};
    background: ${({ $isHeader, $isChanged, $isWarned }) => {
        if ($isHeader) return DIALOG_HEADER_BG;
        if ($isWarned) return '#fff4e5';
        return $isChanged ? DIALOG_CHANGED_BG : 'white';
    }};
    ${({ $isHeader }) => ($isHeader ? 'position: sticky; top: 0; z-index: 1;' : '')}
`;

export const DialogNameCell = styled.div<{ $isHeader?: boolean }>`
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    font-size: ${({ $isHeader }) => ($isHeader ? '13px' : '14px')};
    font-weight: ${({ $isHeader }) => ($isHeader ? 500 : 700)};

    small {
        font-size: 12px;
        font-weight: 400;
        color: ${DIALOG_MUTED};
    }
`;

export const DialogHeaderCell = styled.div`
    padding: 8px 10px;
    border-inline-start: 1px solid ${DIALOG_HEADER_RULE};
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px 8px;
    font-size: 13px;
    font-weight: 500;
`;

// Positioned, so the visually hidden text inside it is placed here and not below the dialog's paper
export const DialogCell = styled.div`
    position: relative;
    padding: 8px 10px;
    border-inline-start: 1px solid ${DIALOG_ROW_BORDER};
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
`;

export const DialogAddressText = styled.div`
    font-size: 12.5px;
    line-height: 1.35;

    span {
        color: ${DIALOG_MUTED};
    }
`;

export const PillsRow = styled.div`
    display: flex;
    gap: 6px;
`;

export const Pill = styled.button<{ $kind: 'pickup' | 'drop'; $pressed?: boolean }>`
    flex: 1;
    // A lone address column stretches wide; the pills stay pill-sized
    max-width: 160px;
    height: 28px;
    padding: 0 8px;
    border-radius: 14px;
    border: 1px solid ${({ $kind, $pressed }) => ($pressed ? PILL[$kind].onBorder : PILL[$kind].border)};
    background: ${({ $kind, $pressed }) => ($pressed ? PILL[$kind].onBg : PILL[$kind].bg)};
    color: ${DIALOG_TEXT};
    font-size: 12px;
    font-weight: ${({ $pressed }) => ($pressed ? 700 : 400)};
    font-family: inherit;
    cursor: pointer;

    // The fills differ little (1.3-1.5:1): a check mark says "chosen" without relying on colour
    ${({ $pressed }) => ($pressed ? `&::before { content: '✓ '; }` : '')}

    &:focus-visible {
        outline: 2px solid #1565c0;
        outline-offset: 2px;
    }
`;

// Pressed while "for all" is what the column holds: a second click then undoes it
export const ForAllPill = styled.button<{ $kind: 'pickup' | 'drop'; $pressed?: boolean }>`
    height: 26px;
    padding: 0 10px;
    border-radius: 13px;
    border: 1px solid ${({ $kind, $pressed }) => ($pressed ? PILL[$kind].onBorder : FOR_ALL[$kind].border)};
    background: ${({ $kind, $pressed }) => ($pressed ? PILL[$kind].onBg : 'white')};
    color: ${({ $kind, $pressed }) => ($pressed ? DIALOG_TEXT : FOR_ALL[$kind].text)};
    font-size: 11.5px;
    font-weight: ${({ $pressed }) => ($pressed ? 700 : 400)};
    font-family: inherit;
    cursor: pointer;

    ${({ $pressed }) => ($pressed ? `&::before { content: '✓ '; }` : '')}

    &:focus-visible {
        outline: 2px solid #1565c0;
        outline-offset: 2px;
    }
`;

export const DialogEmptyCell = styled.div`
    text-align: center;
    font-size: 12px;
    color: ${DIALOG_MUTED};
`;

// Read by a screen reader, not drawn: the dash beside it is only drawn
export const VisuallyHidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
`;

export const DialogLegend = styled.div`
    display: flex;
    gap: 14px;
    font-size: 12.5px;
    color: #424242;
`;

export const DialogLegendItem = styled.span<{ $kind: 'pickup' | 'drop' }>`
    display: flex;
    align-items: center;
    gap: 6px;

    &::before {
        content: '';
        width: 26px;
        height: 14px;
        border-radius: 7px;
        background: ${({ $kind }) => ($kind === 'pickup' ? PILL.pickup.bg : '#FFEDB1')};
        border: 1px solid ${({ $kind }) => FOR_ALL[$kind].border};
    }
`;
